/**
 * Hook useRecipeSubmissionModal
 *
 * Gobierna la apertura/cierre del modal de envío de recetas y su
 * sincronización con la URL `/envio-receta` (destino de un QR impreso:
 * https://www.intercasacanarias.es/envio-receta).
 *
 * POR QUÉ SE USA `window.history.pushState` EN VEZ DE `navigate()`:
 * `ScrollToTop` (frontend/src/components/common/ScrollToTop.jsx) y
 * `ImmersiveLayout` fuerzan un `scrollTo(0, 0)` en cada cambio de
 * `pathname` del router. El botón que abre este modal está al final de
 * la página `/recetas`; si abriéramos el modal con `navigate('/envio-receta')`
 * el router cambiaría de ruta y la página saltaría al inicio, perdiendo la
 * posición de scroll del usuario. Para evitar ese salto, la apertura "en
 * caliente" cambia la URL manualmente con `pushState` (sin que el router
 * reaccione con un scroll-to-top) y solo se usa `navigate` cuando el modal
 * se cierra desde una entrada directa a `/envio-receta` (ahí no hay scroll
 * que preservar, así que el salto no supone un problema).
 *
 * Hay dos formas de llegar al modal abierto:
 * 1. Apertura en caliente: el usuario ya está en `/recetas` y pulsa el
 *    botón. No se navega; se hace `pushState` a `/envio-receta` y se marca
 *    `abierto = true` localmente. Al cerrar, si el estado del historial
 *    indica que fuimos nosotros quienes empujamos la entrada
 *    (`window.history.state?.recipeModal`), se usa `history.back()` para
 *    volver exactamente a donde estaba el usuario.
 * 2. Entrada en frío: el usuario llega directo a `/envio-receta` (desde el
 *    QR). El router ya está en esa ruta al montar, así que el modal debe
 *    nacer abierto. Al cerrar, se navega con `navigate('/recetas', { replace: true })`
 *    porque no hay una entrada de historial "propia" a la que volver.
 *
 * @returns {{ abierto: boolean, abrir: Function, cerrar: Function }}
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLanguageFromPath, localizePath, stripLanguagePrefix } from '../i18n/paths';

/** Ruta que representa el modal de envío de recetas abierto. */
const RUTA_MODAL = '/envio-receta';

/** Ruta a la que se vuelve al cerrar el modal en una entrada en frío. */
const RUTA_RECETAS = '/recetas';

/**
 * Indica si un pathname es el del modal en cualquier idioma
 * (`/envio-receta`, `/en/envio-receta`...). El prefijo de idioma se ignora.
 * @param {string} pathname
 * @returns {boolean}
 */
const esRutaModal = (pathname) => stripLanguagePrefix(pathname) === RUTA_MODAL;

const useRecipeSubmissionModal = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Estado inicial: si el usuario ya llega a /envio-receta (entrada en
  // frío, típicamente desde el QR), el modal debe estar abierto desde el
  // primer render.
  const language = getLanguageFromPath(location.pathname);
  const [abierto, setAbierto] = useState(esRutaModal(location.pathname));

  // Sincroniza `abierto` con la ruta actual: cubre tanto la entrada en frío
  // (primer render en /envio-receta) como cualquier navegación posterior del
  // router hacia o desde esa ruta (por ejemplo, enlaces internos o el botón
  // "atrás"/"adelante" del navegador gestionado por react-router).
  useEffect(() => {
    setAbierto(esRutaModal(location.pathname));
  }, [location.pathname]);

  // Escucha el evento nativo `popstate` para cerrar el modal cuando la URL
  // deja de ser /envio-receta debido a una navegación manual del navegador
  // (por ejemplo, el usuario pulsa "atrás" tras la apertura en caliente,
  // que no pasa por el router porque usamos pushState en vez de navigate).
  useEffect(() => {
    const manejarPopstate = () => {
      if (!esRutaModal(window.location.pathname)) {
        setAbierto(false);
      }
    };

    window.addEventListener('popstate', manejarPopstate);
    return () => window.removeEventListener('popstate', manejarPopstate);
  }, []);

  /**
   * Abre el modal desde /recetas sin provocar el salto de scroll de
   * ScrollToTop: actualiza la URL visible a /envio-receta mediante
   * `pushState` (el router no se entera, así que no se dispara ningún
   * efecto de scroll-to-top) y marca el modal como abierto.
   */
  const abrir = useCallback(() => {
    window.history.pushState({ recipeModal: true }, '', localizePath(RUTA_MODAL, language));
    setAbierto(true);
  }, [language]);

  /**
   * Cierra el modal. Si la entrada actual del historial fue creada por
   * `abrir()` (marcada con `recipeModal: true`), retrocede con
   * `history.back()` para restaurar exactamente la URL y posición previas.
   * En caso contrario (entrada en frío desde el QR, sin una entrada previa
   * propia), navega a /recetas sustituyendo la entrada actual.
   */
  const cerrar = useCallback(() => {
    if (window.history.state?.recipeModal) {
      window.history.back();
    } else {
      navigate(localizePath(RUTA_RECETAS, language), { replace: true });
    }
    setAbierto(false);
  }, [navigate, language]);

  return { abierto, abrir, cerrar };
};

export default useRecipeSubmissionModal;
